import { camelCase, snakeCase } from 'lodash';

import AppError from '@lib/app-error';
import { reportBackendStatus } from '@lib/service-status';
import { DTO } from '@lib/types';

export interface ResponseWrapper<T> {
  data: T;
  headers: Headers;
  status: number;
}

export interface RequestConfig {
  body?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, unknown> | URLSearchParams | string;
  customTimeout?: number;
  signal?: AbortSignal;
}

export type RequestConfigWMethod = RequestConfig & {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
};

const REQUEST_TIMEOUT_MS = 15_000;

interface RequesterInstance {
  download(
    path: string,
    config?: RequestConfig,
  ): Promise<ResponseWrapper<Blob>>;
  delete<T = unknown>(
    path: string,
    config?: RequestConfig,
  ): Promise<ResponseWrapper<T>>;
  get<T = unknown>(
    path: string,
    config?: RequestConfig,
  ): Promise<ResponseWrapper<T>>;
  patch<T = unknown>(
    path: string,
    config?: RequestConfig,
  ): Promise<ResponseWrapper<T>>;
  post<T = unknown>(
    path: string,
    config?: RequestConfig,
  ): Promise<ResponseWrapper<T>>;
  put<T = unknown>(
    path: string,
    config?: RequestConfig,
  ): Promise<ResponseWrapper<T>>;
  request<T = unknown>(
    path: string,
    config?: RequestConfigWMethod,
  ): Promise<ResponseWrapper<T>>;
}

const deeplyApplyKeyTransform = <T>(
  obj: T,
  transformCaseFn: typeof camelCase,
): T => {
  if (Array.isArray(obj)) {
    return obj.map((item) =>
      deeplyApplyKeyTransform(item, transformCaseFn),
    ) as T;
  }

  if (obj !== null && typeof obj === 'object') {
    const ret: Record<string, unknown> = {};

    Object.entries(obj as Record<string, unknown>).forEach(([key, val]) => {
      ret[transformCaseFn(key)] = deeplyApplyKeyTransform(val, transformCaseFn);
    });

    return ret as T;
  }

  return obj;
};

const toQueryString = (params?: RequestConfig['params']): string => {
  if (!params) {
    return '';
  }

  if (typeof params === 'string') {
    return params.startsWith('?') ? params.slice(1) : params;
  }

  if (params instanceof URLSearchParams) {
    return params.toString();
  }

  const searchParams = new URLSearchParams();

  const normalizedParams = deeplyApplyKeyTransform(params as DTO, snakeCase);

  Object.entries(normalizedParams).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, String(item)));
      return;
    }

    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

const buildUrl = (path: string, params?: RequestConfig['params']): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  const queryString = toQueryString(params);

  const separator = normalizedPath.includes('?') ? '&' : '?';

  const suffix = queryString ? `${separator}${queryString}` : '';

  const baseUrl = (process.env.API_URL ?? '').replace(/\/+$/, '');

  return `${baseUrl}/api${normalizedPath}${suffix}`;
};

const toRequestBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined || body === null) {
    return undefined;
  }

  if (
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer
  ) {
    return body as BodyInit;
  }

  if (typeof body === 'string') {
    return body;
  }

  return JSON.stringify(deeplyApplyKeyTransform(body as DTO, snakeCase));
};

const toResponseData = <T>(payload: unknown): T => {
  if (payload !== null && typeof payload === 'object') {
    return deeplyApplyKeyTransform(payload as DTO, camelCase) as T;
  }

  return payload as T;
};

const parseResponseBody = async (response: Response) => {
  const responseText = await response.text();

  if (!responseText) {
    return undefined;
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return responseText;
  }
};

const createHeaders = (headers: Record<string, string> = {}): Headers => {
  const requestHeaders = new Headers({
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...headers,
  });

  return requestHeaders;
};

const handleFailure = async (
  status: number,
  payload: unknown,
  responseHeaders?: Headers,
): Promise<never> => {
  if ([502, 503, 504].includes(status)) {
    reportBackendStatus('unavailable');
  } else {
    reportBackendStatus('available');
  }

  const errorPayload = (payload ?? {}) as Record<string, unknown>;
  const errorId =
    typeof errorPayload.errorId === 'string'
      ? errorPayload.errorId
      : typeof errorPayload.error_id === 'string'
        ? errorPayload.error_id
        : undefined;

  const requestId =
    responseHeaders?.get('X-Request-ID') ??
    (typeof errorPayload.requestId === 'string'
      ? errorPayload.requestId
      : undefined);

  const retryAfter = responseHeaders?.get('Retry-After');
  const retryAfterMs = retryAfter
    ? /^\d+(\.\d+)?$/.test(retryAfter.trim())
      ? Number(retryAfter) * 1000
      : Math.max(0, Date.parse(retryAfter) - Date.now())
    : undefined;

  if (
    errorPayload.code === 'legal-acceptance-required' &&
    typeof window !== 'undefined'
  ) {
    window.dispatchEvent(new Event('nexo:legal-required'));
  }

  throw AppError.fromResponse(
    status,
    String(errorPayload.code ?? 'request-failed'),
    typeof errorPayload.message === 'string' ? errorPayload.message : undefined,
    errorId,
    requestId,
    retryAfterMs !== undefined && Number.isFinite(retryAfterMs)
      ? retryAfterMs
      : undefined,
  );
};

const requestWithTimeout = async <T>(
  url: string,
  options: RequestInit & { customTimeout?: number },
  consume: (response: Response) => Promise<T>,
) => {
  const controller = new AbortController();
  const onAbort = () => controller.abort(options.signal?.reason);
  options.signal?.addEventListener('abort', onAbort, { once: true });

  if (options.signal?.aborted) {
    onAbort();
  }

  const timeout = globalThis.setTimeout(
    () => controller.abort(),
    options.customTimeout ?? REQUEST_TIMEOUT_MS,
  );

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    reportBackendStatus('available');
    return await consume(response);
  } catch (error) {
    if (options.signal?.aborted) {
      throw (
        options.signal.reason ??
        new DOMException('Consulta cancelada.', 'AbortError')
      );
    }

    if (error instanceof AppError) {
      throw error;
    }

    reportBackendStatus('unavailable');
    throw AppError.ServiceUnavailable();
  } finally {
    globalThis.clearTimeout(timeout);
    options.signal?.removeEventListener('abort', onAbort);
  }
};

const download = async (
  path: string,
  config: RequestConfig = {},
): Promise<ResponseWrapper<Blob>> =>
  requestWithTimeout(
    buildUrl(path, config.params),
    {
      credentials: 'include',
      headers: createHeaders(config.headers),
      method: 'GET',
      customTimeout: config.customTimeout,
      signal: config.signal,
    },
    async (response) => {
      if (!response.ok) {
        const payload = await parseResponseBody(response);

        await handleFailure(response.status, payload, response.headers);
      }

      return {
        data: await response.blob(),
        headers: response.headers,
        status: response.status,
      };
    },
  );

async function request<T>(
  path: string,
  config: RequestConfigWMethod = {},
): Promise<ResponseWrapper<T>> {
  const { body, headers = {}, method = 'GET', params } = config;

  const requestHeaders = createHeaders(headers);

  const requestBody = toRequestBody(body);

  if (body instanceof FormData) {
    requestHeaders.delete('Content-Type');
  }

  const fetchOptions: RequestInit = {
    body: requestBody,
    credentials: 'include',
    headers: requestHeaders,
    method,
  };

  return requestWithTimeout(
    buildUrl(path, params),
    {
      ...fetchOptions,
      customTimeout: config.customTimeout,
      signal: config.signal,
    },
    async (response) => {
      const payload = await parseResponseBody(response);

      const responseData = toResponseData<T>(payload);

      if (response.ok) {
        return {
          data: responseData,
          headers: response.headers,
          status: response.status,
        };
      }

      await handleFailure(response.status, payload, response.headers);

      return {
        data: responseData,
        headers: response.headers,
        status: response.status,
      };
    },
  );
}

export const Requester: RequesterInstance = {
  download,
  get: <T = unknown>(path: string, config?: RequestConfig) =>
    request<T>(path, { ...config, method: 'GET' }),
  post: <T = unknown>(path: string, config?: RequestConfig) =>
    request<T>(path, { ...config, method: 'POST' }),
  patch: <T = unknown>(path: string, config?: RequestConfig) =>
    request<T>(path, { ...config, method: 'PATCH' }),
  put: <T = unknown>(path: string, config?: RequestConfig) =>
    request<T>(path, { ...config, method: 'PUT' }),
  delete: <T = unknown>(path: string, config?: RequestConfig) =>
    request<T>(path, { ...config, method: 'DELETE' }),
  request: <T = unknown>(path: string, config?: RequestConfig) =>
    request<T>(path, config),
};
